import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { Contractor } from '../../services/contractorService';
import contractorService from '../../services/contractorService';
import { useNotification } from '../layouts/Layout';

interface EditContractorDialogProps {
  open: boolean;
  onClose: () => void;
  contractor: Contractor | null;
  onSuccess: () => void;
}

const EditContractorDialog: React.FC<EditContractorDialogProps> = ({
  open,
  onClose,
  contractor,
  onSuccess
}) => {
  const [industry, setIndustry] = useState<string>('');
  const [type, setType] = useState<string>('dostawca');
  const [rating, setRating] = useState<string>('średni');
  const [email2, setEmail2] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { showNotification } = useNotification();

  // Inicjalizacja formularza danymi kontrahenta
  useEffect(() => {
    if (contractor) {
      setIndustry(contractor.industry || '');
      setType(contractor.type || 'dostawca');
      setRating(contractor.rating || 'średni');
      setEmail2(contractor.email2 || '');
    }
  }, [contractor]);

  const handleIndustryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIndustry(event.target.value);
  };

  const handleEmail2Change = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail2(event.target.value);
  };

  const handleTypeChange = (event: SelectChangeEvent<string>) => {
    setType(event.target.value);
  };

  const handleRatingChange = (event: SelectChangeEvent<string>) => {
    setRating(event.target.value);
  };

  const handleSubmit = async () => {
    if (!contractor) return;

    setLoading(true);
    try {
      console.log("Wysyłanie danych do aktualizacji:", {
        industry,
        type,
        rating,
        email2: email2 || "" // Upewniamy się, że email2 nie jest null
      });
      
      await contractorService.updateContractor(contractor.id, {
        industry,
        type,
        rating,
        email2: email2 || "" // Upewniamy się, że email2 nie jest null
      });
      showNotification('Kontrahent został zaktualizowany pomyślnie', 'success');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Błąd podczas aktualizacji kontrahenta:', error);
      showNotification('Wystąpił błąd podczas aktualizacji kontrahenta', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edytuj kontrahenta</DialogTitle>
      <DialogContent>
        {contractor && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {contractor.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {contractor.address}, {contractor.location}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              NIP: {contractor.nip}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Email: {contractor.email}
            </Typography>

            <TextField
              margin="dense"
              label="Dodatkowy adres e-mail"
              type="email"
              fullWidth
              variant="outlined"
              value={email2}
              onChange={handleEmail2Change}
              sx={{ mt: 2 }}
              placeholder="Dodatkowy adres e-mail kontrahenta"
              helperText="Ten adres e-mail nie będzie aktualizowany z MSSQL"
            />

            <TextField
              margin="dense"
              label="Branża"
              type="text"
              fullWidth
              variant="outlined"
              value={industry}
              onChange={handleIndustryChange}
              sx={{ mt: 2 }}
            />

            <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
              <InputLabel id="type-label">Grupa</InputLabel>
              <Select
                labelId="type-label"
                value={type}
                label="Grupa"
                onChange={handleTypeChange}
              >
                <MenuItem value="dostawca">Dostawca</MenuItem>
                <MenuItem value="podwykonawca">Podwykonawca</MenuItem>
                <MenuItem value="inwestor">Inwestor</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
              <InputLabel id="rating-label">Ocena</InputLabel>
              <Select
                labelId="rating-label"
                value={rating}
                label="Ocena"
                onChange={handleRatingChange}
              >
                <MenuItem value="dobry">Dobry</MenuItem>
                <MenuItem value="średni">Średni</MenuItem>
                <MenuItem value="słaby">Słaby</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Anuluj
        </Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Zapisz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditContractorDialog;
